/**
 * @file useChat8PerguntasPanel.ts
 * @description Custom hook for Chat8PerguntasPanel state management and business logic
 */

import {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useAppSelector } from "@/store/hooks";
import { useToast } from "@/components/ui/use-toast";
import {
  parseQuestionsFromMessage,
  parseQuestionsFromMessageAdvanced,
  hasQuestions,
  formatQuestions,
} from "@/utils/chat";
import { logger } from "@/utils/logger";
import { TIMEOUT_CONFIG, TIMEOUT_LABELS } from "@/config/timeouts";
import {
  Chat8PerguntasPanelProps,
  Chat8PerguntasPanelHookReturn,
  Chat8PerguntasPanelState,
  Chat8PerguntasPanelActions,
  Chat8PerguntasPanelComputed,
  Answer,
  ParsedContent,
} from "../Chat8PerguntasPanel.types";

/**
 * @critical HOOK PRINCIPAL - NÃO MODIFICAR ESTRUTURA
 * @description Hook que gerencia todo o estado e lógica do painel de perguntas
 *
 * @state CRÍTICO: Gerencia todos os estados do componente
 * @effect CRÍTICO: useEffect que monitora mudanças nas mensagens
 * @selector CRÍTICO: useAppSelector para acessar mensagens do chatSlice
 * @callback CRÍTICO: handleSubmitAnswers para enviar respostas
 *
 * @warning NÃO REMOVER: useAppSelector é OBRIGATÓRIO para sincronização
 * @warning NÃO MODIFICAR: Lógica de extração de perguntas
 * @warning NÃO SUBSTITUIR: Outros hooks não podem substituir esta funcionalidade
 */
export const useChat8PerguntasPanel = (
  props: Chat8PerguntasPanelProps
): Chat8PerguntasPanelHookReturn => {
  const { sessionId, documentId, onResponseGenerated, onError } = props;
  const { toast } = useToast();

  // State management
  const [state, setState] = useState<Chat8PerguntasPanelState>({
    isSubmitting: false,
    answers: {},
    freeFormMessage: "",
    showSplash: false,
    lastAssistantMessageId: null,
    hidePanelAfterSubmit: false,
    isDocumentLoading: false,
    lastDocumentId: null,
    parsedContent: null,
  });

  // Refs
  const firstAnswerRef = useRef<HTMLTextAreaElement>(null);

  // Redux selectors
  const { messages } = useAppSelector((state) => state.chat);
  const { isConnected } = useAppSelector((state) => state.websocket);

  // Memoized computed values
  const computed: Chat8PerguntasPanelComputed = useMemo(() => {
    // Last assistant message
    const lastAssistantMessage = (() => {
      const assistantMessages = messages.filter(
        (msg) => msg.role === "assistant",
      );
      return assistantMessages.length > 0
        ? assistantMessages[assistantMessages.length - 1]
        : null;
    })();

    // Questions, intro, outro from parsed content
    const questions = state.parsedContent?.questions || [];
    const intro = state.parsedContent?.intro || "";
    const outro = state.parsedContent?.outro || "";

    // Should render questions
    const shouldRenderQuestions = (() => {
      const hasQuestions = questions.length > 0;
      const hasIntroContent = !!intro && intro.trim().length > 0;
      const hasOutroContent = !!outro && outro.trim().length > 0;
      const hasContent = hasQuestions || hasIntroContent || hasOutroContent;
      const hasLastAssistantMessage = !!lastAssistantMessage;
      const isNotSubmitting = !state.isSubmitting;

      const result = hasContent && hasLastAssistantMessage && isNotSubmitting;

      return result;
    })();

    // Field validation
    const isAnyFieldFilled = (() => {
      const hasAnswers = Object.values(state.answers).some((answer) => answer.trim().length > 0);
      const hasFreeFormMessage = state.freeFormMessage.trim().length > 0;
      return hasAnswers || hasFreeFormMessage;
    })();

    const isSubmitButtonDisabled = state.isSubmitting;

    return {
      questions,
      intro,
      outro,
      shouldRenderQuestions,
      isAnyFieldFilled,
      isSubmitButtonDisabled,
      lastAssistantMessage,
    };
  }, [messages, state.parsedContent, state.isSubmitting, state.answers, state.freeFormMessage]);

  // Actions
  const actions: Chat8PerguntasPanelActions = {
    handleAnswerChange: useCallback(
      (questionIndex: number, value: string) => {
        setState((prev) => ({
          ...prev,
          answers: {
            ...prev.answers,
            [questionIndex.toString()]: value,
          },
        }));
      },
      [],
    ),

    setFreeFormMessage: useCallback((value: string) => {
      setState((prev) => ({
        ...prev,
        freeFormMessage: value,
      }));
    }, []),

    handleFreeFormMessageChange: useCallback((value: string) => {
      setState((prev) => ({
        ...prev,
        freeFormMessage: value,
      }));
    }, []),

    handleSubmitAnswers: useCallback(async () => {
      logger.debug("[useChat8PerguntasPanel] handleSubmitAnswers iniciado");
      logger.debug("[useChat8PerguntasPanel] Estado atual:", {
        isSubmitting: state.isSubmitting,
        questionsCount: computed.questions.length,
        answersCount: Object.keys(state.answers).length,
        hasFreeFormMessage: !!state.freeFormMessage.trim(),
        hasOnResponseGenerated: !!onResponseGenerated,
      });

      // Verifica se já está enviando
      if (state.isSubmitting) {
        logger.debug(
          "[useChat8PerguntasPanel] handleSubmitAnswers abortado - já enviando",
        );
        return;
      }

      // Verifica se o callback está disponível
      if (!onResponseGenerated) {
        logger.error(
          "[useChat8PerguntasPanel] onResponseGenerated não está disponível!",
        );
        toast({
          title: "Erro",
          description: "Função de envio não está disponível.",
          variant: "destructive",
        });
        return;
      }

      setState((prev) => ({ ...prev, isSubmitting: true }));

      // Ativa o splash animado
      logger.info(
        "[useChat8PerguntasPanel] 🎯 ATIVANDO SPLASH - Aguardando novas perguntas do assistente:",
        {
          timestamp: new Date().toISOString(),
          questionsCount: computed.questions.length,
          answersCount: Object.keys(state.answers).length,
          hasFreeFormMessage: !!state.freeFormMessage.trim(),
        },
      );
      setState((prev) => ({ ...prev, showSplash: true }));

      try {
        let finalMessage = "";

        if (computed.questions.length > 0) {
          logger.debug(
            "[useChat8PerguntasPanel] Construindo resposta com perguntas...",
          );
          // Constrói o bloco de perguntas e respostas.
          const answeredQuestions = computed.questions
            .map((question, index) => {
              let answer = state.answers[index.toString()];
              if (!answer || answer.trim() === "") {
                const textarea = document.getElementById(`question-${index}`) as HTMLTextAreaElement;
                if (textarea) {
                  answer = textarea.value;
                }
              }
              const hasAnswer = answer?.trim();
              logger.debug(`[useChat8PerguntasPanel] Pergunta ${index + 1}:`, {
                question: question.substring(0, 50) + "...",
                hasAnswer,
                answerLength: answer?.length || 0,
              });
              return hasAnswer ? `${question}\nResposta: ${answer}` : null;
            })
            .filter(Boolean);

          logger.debug(
            "[useChat8PerguntasPanel] Perguntas respondidas:",
            answeredQuestions.length,
          );

          const answeredBlock = answeredQuestions.join("\n\n");
          let freeForm = state.freeFormMessage;
          if (!freeForm || freeForm.trim() === "") {
            const freeFormTextarea = document.getElementById("chat8-free-form-message") as HTMLTextAreaElement;
            if (freeFormTextarea) {
              freeForm = freeFormTextarea.value;
            }
          }
          finalMessage = freeForm.trim()
            ? `${answeredBlock}\n\n${freeForm}`
            : answeredBlock;
        } else {
          logger.debug(
            "[useChat8PerguntasPanel] Construindo resposta sem perguntas...",
          );
          // Se não houver perguntas, usa apenas o comentário livre.
          finalMessage = state.freeFormMessage;
        }

        if (!finalMessage.trim()) {
          finalMessage = "Seguir com as configurações padrão";
          logger.debug("[useChat8PerguntasPanel] Mensagem final estava vazia, usando fallback: " + finalMessage);
        }

        logger.debug("[useChat8PerguntasPanel] Mensagem final construída:", {
          length: finalMessage.length,
          preview: finalMessage.substring(0, 200) + "...",
          hasOnResponseGenerated: !!onResponseGenerated,
        });

        logger.info("[useChat8PerguntasPanel] 🚀 CHAMANDO onResponseGenerated...");
        logger.info("[useChat8PerguntasPanel] 📤 ENVIANDO MENSAGEM PARA O ASSISTENTE:", {
          messageLength: finalMessage.length,
          messagePreview: finalMessage.substring(0, 200) + "...",
          timestamp: new Date().toISOString(),
          isSubmitting: state.isSubmitting,
          showSplash: state.showSplash,
        });

        onResponseGenerated(finalMessage);

        logger.info(
          "[useChat8PerguntasPanel] ✅ onResponseGenerated chamado com sucesso - AGUARDANDO RESPOSTA DO ASSISTENTE",
        );

        // Limpa o formulário após o envio.
        setState((prev) => ({
          ...prev,
          answers: {},
          freeFormMessage: "",
          hidePanelAfterSubmit: true,
        }));

        logger.info(
          "[useChat8PerguntasPanel] 📝 Formulário limpo - SPLASH ATIVO AGUARDANDO RESPOSTA DO ASSISTENTE",
        );

        toast({
          title: "Resposta enviada",
          description: "Suas respostas foram enviadas ao assistente.",
        });
      } catch (error) {
        logger.error("[useChat8PerguntasPanel] Erro ao enviar respostas:", error);
        onError?.("Erro ao compilar e enviar respostas.");
        toast({
          title: "Erro",
          description:
            "Não foi possível enviar suas respostas. Tente novamente.",
          variant: "destructive",
        });

        // ✅ Apenas em caso de erro, desativar isSubmitting e splash
        setState((prev) => ({
          ...prev,
          isSubmitting: false,
          showSplash: false
        }));
      }

      // ✅ REMOVIDO: finally block que desativava isSubmitting prematuramente
      // O controle agora é feito pelos effects que monitoram mensagens do WebSocket
      logger.debug("[useChat8PerguntasPanel] handleSubmitAnswers finalizado - aguardando WebSocket");
    }, [
      state.isSubmitting,
      state.answers,
      state.freeFormMessage,
      computed.questions,
      onResponseGenerated,
      onError,
      toast,
    ]),

    resetForm: useCallback(() => {
      setState((prev) => ({
        ...prev,
        answers: {},
        freeFormMessage: "",
        hidePanelAfterSubmit: false,
      }));
    }, []),

    setShowSplash: useCallback((show: boolean) => {
      setState((prev) => ({ ...prev, showSplash: show }));
    }, []),

    setHidePanelAfterSubmit: useCallback((hide: boolean) => {
      setState((prev) => ({ ...prev, hidePanelAfterSubmit: hide }));
    }, []),

    setIsDocumentLoading: useCallback((loading: boolean) => {
      setState((prev) => ({ ...prev, isDocumentLoading: loading }));
    }, []),
  };

  // Effects
  useEffect(() => {
    const parseContent = async () => {
      if (!computed.lastAssistantMessage?.content) {
        logger.debug("[useChat8PerguntasPanel] Nenhuma mensagem do assistente para processar");
        setState((prev) => ({ ...prev, parsedContent: null }));
        return;
      }

      logger.debug("[useChat8PerguntasPanel] Processando mensagem do assistente:", {
        messageId: computed.lastAssistantMessage.id,
        contentLength: computed.lastAssistantMessage.content.length,
        contentPreview: computed.lastAssistantMessage.content.substring(0, 200) + "...",
      });

      try {
        const parsed = await parseQuestionsFromMessageAdvanced(
          computed.lastAssistantMessage.content,
          computed.lastAssistantMessage.id,
        );

        logger.debug("[useChat8PerguntasPanel] Conteúdo parseado:", {
          hasIntro: !!parsed.intro,
          introLength: parsed.intro?.length || 0,
          questionsCount: parsed.questions.length,
          questions: parsed.questions,
          hasOutro: !!parsed.outro,
          outroLength: parsed.outro?.length || 0,
        });

        setState((prev) => ({ ...prev, parsedContent: parsed }));
      } catch (error) {
        logger.error(
          "[useChat8PerguntasPanel] Erro ao processar mensagem do assistente:",
          {
            error,
            messageId: computed.lastAssistantMessage?.id,
            messageContent: computed.lastAssistantMessage?.content,
          },
        );
        setState((prev) => ({ ...prev, parsedContent: null }));
      }
    };
    parseContent();
  }, [computed.lastAssistantMessage?.content, computed.lastAssistantMessage?.id]);

  // Focus and scroll effect
  useEffect(() => {
    if (computed.questions.length > 0 && firstAnswerRef.current) {
      // Pequeno delay para garantir que o DOM foi renderizado
      const timer = setTimeout(() => {
        if (firstAnswerRef.current) {
          // Foca no primeiro campo de resposta
          firstAnswerRef.current.focus();

          // Faz scroll suave para enquadrar o elemento na tela
          firstAnswerRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center", // Centraliza o elemento na tela
            inline: "nearest",
          });

          logger.debug(
            "[useChat8PerguntasPanel] Foco e scroll aplicados na primeira caixa de resposta",
          );
        }
      }, 100); // 100ms de delay

      return () => clearTimeout(timer);
    }
  }, [computed.questions.length]);

  // Splash control effect
  useEffect(() => {
    // Só executa se o splash estiver ativo
    if (!state.showSplash) {
      logger.debug(
        "[useChat8PerguntasPanel] Splash não está ativo, não iniciando timeout",
      );
      return;
    }

    // Verifica se há uma nova mensagem do assistente
    if (messages && messages.length > 0) {
      const assistantMessages = messages.filter(
        (msg) => msg.role === "assistant",
      );
      const lastAssistantMessage =
        assistantMessages[assistantMessages.length - 1];

      if (
        lastAssistantMessage &&
        lastAssistantMessage.id !== state.lastAssistantMessageId
      ) {
        logger.info(
          "[useChat8PerguntasPanel] 🎯 NOVA MENSAGEM DO ASSISTENTE - DESATIVANDO SPLASH:",
          {
            messageId: lastAssistantMessage.id,
            previousId: state.lastAssistantMessageId,
            timestamp: new Date().toISOString(),
            messagePreview:
              lastAssistantMessage.content?.substring(0, 100) + "...",
          },
        );

        // Atualiza o ID da última mensagem
        setState((prev) => ({ ...prev, lastAssistantMessageId: lastAssistantMessage.id }));

        // Desativa o splash com delay suave
        setTimeout(() => {
          logger.info(
            "[useChat8PerguntasPanel] 🎯 DESATIVANDO SPLASH (com delay de 500ms)",
          );
          setState((prev) => ({ ...prev, showSplash: false }));
        }, 500);
      }
    }
  }, [messages, state.showSplash, state.lastAssistantMessageId]);

  // ✅ MELHORADO: Effect para detectar novas mensagens do assistente
  useEffect(() => {
    // Log detalhado de cada execução do effect
    logger.debug(
      "[useChat8PerguntasPanel] 🔍 EFFECT EXECUTADO - Verificando novas mensagens:",
      {
        isSubmitting: state.isSubmitting,
        showSplash: state.showSplash,
        messagesCount: messages?.length || 0,
        lastAssistantMessageId: state.lastAssistantMessageId,
        timestamp: new Date().toISOString(),
      },
    );

    // Só executa se isSubmitting estiver ativo OU showSplash estiver ativo
    if (!state.isSubmitting && !state.showSplash) {
      logger.debug(
        "[useChat8PerguntasPanel] ❌ ABORTANDO - isSubmitting e showSplash não estão ativos",
      );
      return;
    }

    // Verifica se há mensagens
    if (!messages || messages.length === 0) {
      logger.debug(
        "[useChat8PerguntasPanel] ❌ ABORTANDO - Nenhuma mensagem disponível",
      );
      return;
    }

    // Filtra mensagens do assistente com conteúdo válido
    const assistantMessages = messages.filter(
      (msg) => msg.role === "assistant" && msg.content && msg.content.trim() !== ""
    );

    logger.debug(
      "[useChat8PerguntasPanel] 📊 ANÁLISE DE MENSAGENS:",
      {
        totalMessages: messages.length,
        assistantMessagesCount: assistantMessages.length,
        lastAssistantMessageId: state.lastAssistantMessageId,
        assistantMessageIds: assistantMessages.map(msg => msg.id),
      },
    );

    const lastAssistantMessage = assistantMessages[assistantMessages.length - 1];

    if (!lastAssistantMessage) {
      logger.debug(
        "[useChat8PerguntasPanel] ❌ ABORTANDO - Nenhuma mensagem do assistente encontrada",
      );
      return;
    }

    // Verifica se é uma nova mensagem
    if (lastAssistantMessage.id === state.lastAssistantMessageId) {
      logger.debug(
        "[useChat8PerguntasPanel] ❌ ABORTANDO - Mesma mensagem do assistente (não é nova):",
        {
          messageId: lastAssistantMessage.id,
          lastAssistantMessageId: state.lastAssistantMessageId,
        },
      );
      return;
    }

    // ✅ NOVA MENSAGEM DETECTADA!
    logger.info(
      "[useChat8PerguntasPanel] 🎯 NOVA MENSAGEM DO ASSISTENTE DETECTADA - Processando:",
      {
        messageId: lastAssistantMessage.id,
        previousId: state.lastAssistantMessageId,
        isSubmitting: state.isSubmitting,
        showSplash: state.showSplash,
        timestamp: new Date().toISOString(),
        messagePreview:
          lastAssistantMessage.content?.substring(0, 100) + "...",
        contentLength: lastAssistantMessage.content?.length || 0,
      },
    );

    // ✅ CRÍTICO: Desativa isSubmitting e splash quando resposta do assistente chegar
    setState((prev) => ({
      ...prev,
      isSubmitting: false,
      showSplash: false,
      lastAssistantMessageId: lastAssistantMessage.id
    }));

    logger.info(
      "[useChat8PerguntasPanel] ✅ isSubmitting e splash desativados com sucesso - fluxo WebSocket completo",
      {
        newLastAssistantMessageId: lastAssistantMessage.id,
        previousLastAssistantMessageId: state.lastAssistantMessageId,
        timestamp: new Date().toISOString(),
      }
    );
  }, [messages, state.isSubmitting, state.showSplash]); // ✅ REMOVIDO state.lastAssistantMessageId das dependências

  // Splash timeout effect
  useEffect(() => {
    if (state.showSplash) {
      logger.info(
        `[useChat8PerguntasPanel] 🚀 INICIANDO TIMEOUT DE SEGURANÇA (${TIMEOUT_LABELS.SPLASH_SAFETY}):`,
        {
          timestamp: new Date().toISOString(),
          lastAssistantMessageId: state.lastAssistantMessageId,
          messagesCount: messages.length,
        },
      );

      const timeout = setTimeout(() => {
        logger.warn(
          `[useChat8PerguntasPanel] ⚠️ TIMEOUT DE SEGURANÇA ATINGIDO - Desativando splash:`,
          {
            timestamp: new Date().toISOString(),
            elapsed: TIMEOUT_LABELS.SPLASH_SAFETY,
          },
        );
        setState((prev) => ({
          ...prev,
          showSplash: false,
          isSubmitting: false
        }));
      }, TIMEOUT_CONFIG.SPLASH_SAFETY);

      return () => {
        logger.debug("[useChat8PerguntasPanel] 🧹 TIMEOUT DE SEGURANÇA LIMPO:", {
          timestamp: new Date().toISOString(),
          reason: state.showSplash ? "splash desativado" : "componente desmontado",
        });
        clearTimeout(timeout);
      };
    }
  }, [state.showSplash, state.lastAssistantMessageId, messages.length]);

  // Reset panel after new questions effect
  useEffect(() => {
    if (state.hidePanelAfterSubmit && messages && messages.length > 0) {
      // Verifica se há uma nova mensagem do assistente
      const assistantMessages = messages.filter(
        (msg) => msg.role === "assistant",
      );
      const lastAssistantMessage =
        assistantMessages[assistantMessages.length - 1];

      // Se há uma mensagem do assistente e ela é diferente da última conhecida
      if (lastAssistantMessage && lastAssistantMessage.id !== state.lastAssistantMessageId) {
        logger.info(
          "[useChat8PerguntasPanel] 🎯 NOVAS PERGUNTAS CHEGARAM - Resetando painel:",
          {
            previousMessageId: state.lastAssistantMessageId,
            newMessageId: lastAssistantMessage.id,
            timestamp: new Date().toISOString(),
            messagesCount: messages.length,
            assistantMessagesCount: assistantMessages.length,
          },
        );

        // Reseta o estado para permitir que o painel seja exibido novamente
        setState((prev) => ({
          ...prev,
          hidePanelAfterSubmit: false,
          lastAssistantMessageId: lastAssistantMessage.id,
        }));
      }
    }
  }, [messages, state.hidePanelAfterSubmit, state.lastAssistantMessageId]);

  // ✅ REATIVADO: Effect para detectar mudanças de documento
  useEffect(() => {
    // Se o documento mudou, ativa o estado de carregamento
    if (documentId && documentId !== state.lastDocumentId) {
      logger.info("[useChat8PerguntasPanel] Documento mudou, ativando loading:", {
        previousDocumentId: state.lastDocumentId,
        newDocumentId: documentId,
        timestamp: new Date().toISOString(),
      });

      setState((prev) => ({
        ...prev,
        isDocumentLoading: true,
        lastDocumentId: documentId,
        parsedContent: null,
        answers: {},
        freeFormMessage: "",
        hidePanelAfterSubmit: false,
        showSplash: false,
      }));
    }
  }, [documentId, state.lastDocumentId]);

  // ✅ REATIVADO: Effect para desativar loading quando mensagens chegam
  useEffect(() => {
    if (state.isDocumentLoading && messages && messages.length > 0) {
      const assistantMessages = messages.filter(
        (msg) => msg.role === "assistant",
      );

      if (assistantMessages.length > 0) {
        const lastAssistantMessage = assistantMessages[assistantMessages.length - 1];

        // Se a mensagem é do documento atual, desativa o loading
        if (lastAssistantMessage.document_id === documentId) {
          logger.info("[useChat8PerguntasPanel] Mensagem do assistente recebida, desativando loading:", {
            messageId: lastAssistantMessage.id,
            documentId: documentId,
            timestamp: new Date().toISOString(),
          });

          setState((prev) => ({ ...prev, isDocumentLoading: false }));
        }
      }
    }
  }, [state.isDocumentLoading, messages, documentId]);

  // ✅ REATIVADO: Effect para timeout de segurança do loading
  useEffect(() => {
    if (state.isDocumentLoading) {
      logger.info(`[useChat8PerguntasPanel] Iniciando timeout de segurança para loading do documento (${TIMEOUT_LABELS.DOCUMENT_LOADING})`);

      const timeout = setTimeout(() => {
        logger.warn(`[useChat8PerguntasPanel] ⚠️ Timeout de segurança atingido: desativando loading do documento após ${TIMEOUT_LABELS.DOCUMENT_LOADING}`, {
          documentId,
          timestamp: new Date().toISOString(),
          elapsed: TIMEOUT_LABELS.DOCUMENT_LOADING
        });
        setState((prev) => ({ ...prev, isDocumentLoading: false }));
      }, TIMEOUT_CONFIG.DOCUMENT_LOADING);

      return () => {
        logger.debug("[useChat8PerguntasPanel] Timeout de loading limpo");
        clearTimeout(timeout);
      };
    }
  }, [state.isDocumentLoading, documentId]);

  // Hide panel timeout
  useEffect(() => {
    if (state.hidePanelAfterSubmit) {
      const timeout = setTimeout(() => {
        logger.warn(`[useChat8PerguntasPanel] Timeout de segurança: resetando painel escondido após ${TIMEOUT_LABELS.PANEL_HIDDEN}`);
        setState((prev) => ({ ...prev, hidePanelAfterSubmit: false }));
      }, TIMEOUT_CONFIG.PANEL_HIDDEN);

      return () => clearTimeout(timeout);
    }
  }, [state.hidePanelAfterSubmit]);

  // ✅ NOVO: Monitor de estado detalhado para debugging
  useEffect(() => {
    logger.info("[MONITOR] Estado do painel de perguntas atualizado:", {
      isSubmitting: state.isSubmitting,
      showSplash: state.showSplash,
      messagesCount: messages.length,
      lastAssistantMessageId: state.lastAssistantMessageId,
      hidePanelAfterSubmit: state.hidePanelAfterSubmit,
      isDocumentLoading: state.isDocumentLoading,
      timestamp: new Date().toISOString(),
      sessionId,
      documentId,
    });

    // Log detalhado das mensagens do assistente
    if (messages && messages.length > 0) {
      const assistantMessages = messages.filter((msg) => msg.role === "assistant");
      logger.info("[MONITOR] Mensagens do assistente:", {
        totalMessages: messages.length,
        assistantMessagesCount: assistantMessages.length,
        lastAssistantMessage: assistantMessages[assistantMessages.length - 1] ? {
          id: assistantMessages[assistantMessages.length - 1].id,
          contentPreview: assistantMessages[assistantMessages.length - 1].content?.substring(0, 100) + "...",
          timestamp: assistantMessages[assistantMessages.length - 1].timestamp,
        } : null,
        lastAssistantMessageId: state.lastAssistantMessageId,
      });
    }
  }, [state.isSubmitting, state.showSplash, messages, state.lastAssistantMessageId, state.hidePanelAfterSubmit, state.isDocumentLoading, sessionId, documentId]);

  // ✅ NOVO: Monitor específico para mudanças de mensagens
  useEffect(() => {
    logger.debug("[MONITOR] 🔍 Monitor de mensagens executado:", {
      messagesCount: messages?.length || 0,
      lastAssistantMessageId: state.lastAssistantMessageId,
      isSubmitting: state.isSubmitting,
      showSplash: state.showSplash,
      timestamp: new Date().toISOString(),
    });

    if (messages && messages.length > 0) {
      const assistantMessages = messages.filter((msg) => msg.role === "assistant");
      const lastAssistantMessage = assistantMessages[assistantMessages.length - 1];

      logger.debug("[MONITOR] 📊 Análise de mensagens do assistente:", {
        totalMessages: messages.length,
        assistantMessagesCount: assistantMessages.length,
        lastAssistantMessageId: state.lastAssistantMessageId,
        lastAssistantMessage: lastAssistantMessage ? {
          id: lastAssistantMessage.id,
          contentPreview: lastAssistantMessage.content?.substring(0, 100) + "...",
          timestamp: lastAssistantMessage.timestamp,
        } : null,
      });

      if (lastAssistantMessage && lastAssistantMessage.id !== state.lastAssistantMessageId) {
        logger.info("[MONITOR] 🎯 NOVA MENSAGEM DO ASSISTENTE DETECTADA:", {
          newMessageId: lastAssistantMessage.id,
          previousMessageId: state.lastAssistantMessageId,
          isSubmitting: state.isSubmitting,
          showSplash: state.showSplash,
          contentPreview: lastAssistantMessage.content?.substring(0, 100) + "...",
          contentLength: lastAssistantMessage.content?.length || 0,
          timestamp: new Date().toISOString(),
        });

        // ✅ ATUALIZAR O lastAssistantMessageId NO MONITOR TAMBÉM
        setState((prev) => ({
          ...prev,
          lastAssistantMessageId: lastAssistantMessage.id
        }));

        logger.info("[MONITOR] ✅ lastAssistantMessageId atualizado:", {
          newLastAssistantMessageId: lastAssistantMessage.id,
          timestamp: new Date().toISOString(),
        });
      }
    }
  }, [messages, state.lastAssistantMessageId, state.isSubmitting, state.showSplash]);

  // ✅ NOVO: Effect específico para debug do splash
  useEffect(() => {
    if (state.showSplash) {
      logger.info("[SPLASH DEBUG] 🎯 SPLASH ATIVO - Monitorando desativação:", {
        isSubmitting: state.isSubmitting,
        showSplash: state.showSplash,
        messagesCount: messages?.length || 0,
        lastAssistantMessageId: state.lastAssistantMessageId,
        timestamp: new Date().toISOString(),
      });

      // Verificar mensagens do assistente a cada 1 segundo
      const interval = setInterval(() => {
        if (messages && messages.length > 0) {
          const assistantMessages = messages.filter((msg) => msg.role === "assistant");
          const lastAssistantMessage = assistantMessages[assistantMessages.length - 1];

          logger.debug("[SPLASH DEBUG] 🔍 Verificação periódica:", {
            totalMessages: messages.length,
            assistantMessagesCount: assistantMessages.length,
            lastAssistantMessageId: state.lastAssistantMessageId,
            lastAssistantMessage: lastAssistantMessage ? {
              id: lastAssistantMessage.id,
              contentPreview: lastAssistantMessage.content?.substring(0, 50) + "...",
            } : null,
            isSubmitting: state.isSubmitting,
            showSplash: state.showSplash,
            timestamp: new Date().toISOString(),
          });

          // Se há uma nova mensagem do assistente, desativar splash
          if (lastAssistantMessage && lastAssistantMessage.id !== state.lastAssistantMessageId) {
            logger.info("[SPLASH DEBUG] 🎯 NOVA MENSAGEM DETECTADA - DESATIVANDO SPLASH:", {
              newMessageId: lastAssistantMessage.id,
              previousMessageId: state.lastAssistantMessageId,
              contentPreview: lastAssistantMessage.content?.substring(0, 100) + "...",
              timestamp: new Date().toISOString(),
            });

            setState((prev) => ({
              ...prev,
              isSubmitting: false,
              showSplash: false,
              lastAssistantMessageId: lastAssistantMessage.id
            }));

            logger.info("[SPLASH DEBUG] ✅ SPLASH DESATIVADO COM SUCESSO");
          }
        }
      }, 1000);

      return () => {
        logger.debug("[SPLASH DEBUG] 🧹 Intervalo de verificação limpo");
        clearInterval(interval);
      };
    }
  }, [state.showSplash, messages, state.lastAssistantMessageId, state.isSubmitting]);

  return {
    state,
    actions,
    computed,
    firstAnswerRef,
    isConnected,
  };
}; 